import { useEffect, useState } from "react"
import { useAuth } from "@/hooks/use-permissions"
import { fetchNoteMentionPeople } from "@/lib/notes-api"
import type { TaskMentionPerson } from "@/types/tasks"

const PEOPLE_TTL_MS = 5 * 60 * 1000

type CacheEntry = { at: number; promise: Promise<TaskMentionPerson[]> }

const peopleCache = new Map<string, CacheEntry>()
const NO_PEOPLE: TaskMentionPerson[] = []

function cachedPeople(key: string): Promise<TaskMentionPerson[]> {
  const cached = peopleCache.get(key)
  if (cached && Date.now() - cached.at < PEOPLE_TTL_MS) return cached.promise
  const promise = fetchNoteMentionPeople()
  peopleCache.set(key, { at: Date.now(), promise })
  promise.catch(() => {
    if (peopleCache.get(key)?.promise === promise) peopleCache.delete(key)
  })
  return promise
}

/** Люди текущей компании для меню «@». Один запрос на компанию, без каталога проектов. */
export function useNoteMentionPeople(): TaskMentionPerson[] {
  const auth = useAuth()
  const companyId = auth?.company?.id ?? null
  const userId = auth?.user?.id ?? null
  const key = companyId == null || userId == null ? null : `${companyId}:${userId}`
  const [loaded, setLoaded] = useState<{ key: string; rows: TaskMentionPerson[] } | null>(null)

  useEffect(() => {
    if (key == null) return
    let active = true
    cachedPeople(key)
      .then((rows) => {
        if (active) setLoaded({ key, rows })
      })
      .catch(() => undefined)
    return () => {
      active = false
    }
  }, [key])

  return loaded != null && loaded.key === key ? loaded.rows : NO_PEOPLE
}
